// BYOK keys data layer. Reads `GET /v1/billing/keys` (masked) and drives the
// capture / verify / revoke mutations — the route renders whatever comes back.
// Mirrors `lib/onboarding.ts`: round-trip over `apiFetch` reusing the dev
// session bearer. A verify (`POST /:id/verify`, #105) writes NO onboarding
// state (#4613): `UPSTREAM_LADDER`'s `PLAN_SELECTED → KEY_SET` rung reads the
// `valid` org key as `key_set` on the next funnel settle (see `verifyKey`).

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { type ProviderModelWireRow, providerModelOption } from './contextWindow';
import { type KeyCaptureAnswer, parseKeyCapture } from './key-capture';
import { type KeyVerifyAnswer, parseKeyVerify } from './key-verify';
import type {
  AddKeyInput,
  KeyCapacityRead,
  KeyRow,
  ProbeModelsInput,
  ProviderModelOption,
} from './keys-types';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

// The row/body shapes live in `./keys-types` and are re-exported here, so
// `from '../lib/keys'` stays the one address every consumer already imports.
export type {
  AddKeyInput,
  AddKeyModelInput,
  KeyCapacityRead,
  KeyCapacityReadRow,
  KeyModelRow,
  KeyRow,
  ProbeModelsInput,
  ProviderModelOption,
} from './keys-types';

/**
 * Ask a provider which models a RAW key can address, before anything is stored.
 * Not knowing the id is the whole reason this endpoint exists: free text at
 * capture is how prod accumulated `gpt-3.5-turbo`, `kimi-k2`, `glm-4.6` and
 * `glm-4.7` on live rows.
 *
 * The wire answers `Page<{id, context_length?}>` — ROWS, not bare strings, and
 * the rows are what the picker offers: the provider-reported context window
 * rides all the way into the capture body, because the dashboard is the only
 * producer of a real window for a dashboard-captured key. Refusals arrive as
 * `ApiError` and are framed by `lib/keyProbeRefusal.ts`; nothing here inspects
 * them.
 *
 * `path` is a parameter because the two pools authorize differently and neither
 * may borrow the other's scope: the org pool spends `write:billing`, which is
 * admin-only, and a plain member capturing a PERSONAL key does not hold it.
 */
export async function probeModelsAt(
  path: string,
  input: ProbeModelsInput,
  token: string | null,
): Promise<readonly ProviderModelOption[]> {
  const page = await apiFetch<Page<ProviderModelWireRow>>(path, token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return page.data.map(providerModelOption);
}

/** Enumerate a raw key's models for the ORG pool (`write:billing`). */
export function probeProviderModels(
  input: ProbeModelsInput,
  token: string | null = SESSION_TOKEN,
): Promise<readonly ProviderModelOption[]> {
  return probeModelsAt('/v1/billing/providers/models', input, token);
}

export const KEYS_QUERY_KEY = 'account_keys';

/** `useKeys`'s cache key — also what the priority edit patches optimistically. */
export const keysQueryKey = (token: string | null): readonly unknown[] => [
  KEYS_QUERY_KEY,
  token,
  activeAccountId(),
];

/**
 * The caller's BYOK keys. Disabled (idle) when there's no session, like `useMe`.
 * Revoked keys are dropped: revoke is the "remove" action, so a revoked key
 * disappears from the list rather than lingering as a dead card (the row is
 * soft-revoked server-side; `invalid` keys stay visible so a bad key is seen).
 *
 * The wire is the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null — the pool is served whole — so this stays a plain query rather
 * than `usePagedQuery`, and no Load-more is offered.
 */
export function useKeys(token: string | null = SESSION_TOKEN, wanted: () => boolean = () => true) {
  return createQuery<readonly KeyRow[]>(() => ({
    queryKey: keysQueryKey(token),
    // `wanted` (#6753): the Fleet page reads the keys only for an admin, whose epic model
    // pickers offer them; a reader is never sent a keys read for a control it cannot see.
    enabled: token !== null && wanted(),
    queryFn: async () =>
      (await apiFetch<Page<KeyRow>>('/v1/billing/keys', token)).data.filter(
        (k) => k.status !== 'revoked',
      ),
  }));
}

/**
 * Capture a raw provider key. The 201 is the masked row AFTER the inline verify
 * plus its `verification` (plan 102 slice 05, `lib/key-capture.ts`).
 */
export async function addKey(
  input: AddKeyInput,
  token: string | null = SESSION_TOKEN,
): Promise<KeyCaptureAnswer> {
  return parseKeyCapture(
    await apiFetch<unknown>('/v1/billing/keys', token, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

/**
 * Verify an org key against its provider (`POST /:id/verify`, #105). Returns the
 * 200's `{ outcome, reason, fault }` (`lib/key-verify.ts`: only `verified` is a pass,
 * a body without one throws); a 422 (`{...row, reason}`) throws `ApiError` — the caller
 * refetches and the card reflects the persisted `invalid` status. A successful
 * verify advances NO onboarding state (#4613): it leaves the org pool holding a
 * `valid` key, which the server's `UPSTREAM_LADDER` `PLAN_SELECTED → KEY_SET`
 * rung reads as `keySet` on the next funnel settle (e.g. `GET
 * /v1/onboarding/state`) — the funnel moves on that read, not on this call.
 */
export async function verifyKey(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<KeyVerifyAnswer> {
  return parseKeyVerify(
    await apiFetch<unknown>(`/v1/billing/keys/${id}/verify`, token, { method: 'POST' }),
  );
}

/** Soft-revoke a key the caller owns. Returns the revoked row. */
export async function revokeKey(id: string, token: string | null = SESSION_TOKEN): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/billing/keys/${id}`, token, { method: 'DELETE' });
}

/**
 * Swap an org key's raw material in place (`POST /:id/rotate`, #971). 200 →
 * the masked row (status `valid`). A 422 problem's `detail` names the
 * provider's refusal — and means the OLD key was untouched and is still live.
 */
export async function rotateKey(
  id: string,
  input: { readonly key: string },
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/billing/keys/${id}/rotate`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Re-stamp an org key's billing nature (`PATCH /:id/kind`, #1247). 200 → the
 * masked row. A 404 means the key id is not this account's (or `revoked`); a
 * 400 names the Zod refusal.
 */
export async function updateKeyKind(
  id: string,
  kind: 'metered' | 'subscription',
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/billing/keys/${id}/kind`, token, {
    method: 'PATCH',
    body: JSON.stringify({ kind }),
  });
}

/**
 * Re-rank an org key in the pool (`PATCH /:id/priority`, plan 102 slice 05b;
 * owner/admin, `write:billing`). 200 → the masked row. A 400 names the Zod
 * refusal (outside 0..10000); a 404 means the id is not this account's.
 */
export async function setKeyPriority(
  id: string,
  priority: number,
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/billing/keys/${id}/priority`, token, {
    method: 'PATCH',
    body: JSON.stringify({ priority }),
  });
}

/**
 * Declare an org key's concurrency (`PATCH /:id/capacity`, #6863; owner/admin,
 * `write:billing`). `null` clears it, so the default for its kind applies. 200 →
 * the masked row; a 400 names the Zod refusal (outside 1..256).
 */
export async function setKeyCapacity(
  id: string,
  capacity: number | null,
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/billing/keys/${id}/capacity`, token, {
    method: 'PATCH',
    body: JSON.stringify({ capacity }),
  });
}

export const KEY_CAPACITY_QUERY_KEY = 'account_key_capacity';

/** `useKeyCapacityRead`'s cache key — keyed by the active org like the keys list. */
export const keyCapacityQueryKey = (token: string | null): readonly unknown[] => [
  KEY_CAPACITY_QUERY_KEY,
  token,
  activeAccountId(),
];

/**
 * The account's key concurrency (`GET /v1/billing/capacity`, #6862, `read:billing`):
 * per admission key its running units, waiting tasks and effective capacity. A 503
 * (ledger unreadable) throws `ApiError`. A body with no `keys` array is refused HERE,
 * at the seam — read as "no row" it would tell every card it is not taking jobs.
 */
export async function getKeyCapacity(
  token: string | null = SESSION_TOKEN,
): Promise<KeyCapacityRead> {
  const body = await apiFetch<Partial<KeyCapacityRead> | null>('/v1/billing/capacity', token);
  if (!body || !Array.isArray(body.keys)) {
    throw new Error('GET /v1/billing/capacity answered without a `keys` array');
  }
  return { keys: body.keys };
}
